import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CfaData,
  CustomerData,
  getWorkspaceId,
  useLazyGetCfaListQuery,
  useLazyGetCustomerListQuery,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { PricingType } from '../../constants';
import { useDebounceSearch, usePaginatedList } from '../../hooks';

export const PRICING_SCREEN_TEXTS = {
  [PricingType.CFA]: {
    title: 'CFA Pricing',
    selectHeading: 'SELECT A CFA',
    selectSubHeading:
      'Select a CFA to view and edit assigned products and prices',
    emptyStateMessage: 'No CFAs Found',
    searchPlaceholder: 'Search by CFA name',
  },
  [PricingType.CUSTOMER]: {
    title: 'Customer Pricing',
    selectHeading: 'SELECT A CUSTOMER',
    selectSubHeading:
      'Select a customer to view and edit assigned products and prices',
    emptyStateMessage: 'No Customers Found',
    searchPlaceholder: 'Search by customer name',
  },
};

export const usePricingData = (pathname: string) => {
  const [showSearch, setShowSearch] = useState(false);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [getCustomerListApi, customerListApiResponse] =
    useLazyGetCustomerListQuery();
  const [getCfaListApi, cfaListApiResponse] = useLazyGetCfaListQuery();

  const pricingType = useMemo(() => {
    return pathname.includes(PricingType.CFA)
      ? PricingType.CFA
      : PricingType.CUSTOMER;
  }, [pathname]);

  const { debouncedSearchValue } = useDebounceSearch();

  const fetchCustomersOrCfas = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      if (pricingType === PricingType.CFA) {
        getCfaListApi({
          workspaceId: sellerWorkspaceId,
          ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
          ...params,
        });
      } else if (pricingType === PricingType.CUSTOMER) {
        getCustomerListApi({
          workspaceId: sellerWorkspaceId,
          ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
          ...params,
        });
      }
    },
    [debouncedSearchValue, pricingType]
  );

  const memoizedCustomerData = useMemo(() => {
    return customerListApiResponse?.data?.customers ?? [];
  }, [customerListApiResponse?.data?.customers]);

  const memoizedCfaData = useMemo(() => {
    return cfaListApiResponse?.data?.cfas ?? [];
  }, [cfaListApiResponse?.data?.cfas]);

  const {
    data: customerOrCfaData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    resetAll,
    isEndOfList,
  } = usePaginatedList<CustomerData | CfaData>({
    fetchDataApiCall: fetchCustomersOrCfas,
    apiResponseData:
      pricingType === PricingType.CFA ? memoizedCfaData : memoizedCustomerData,
    isLoading:
      pricingType === PricingType.CFA
        ? cfaListApiResponse.isLoading
        : customerListApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [debouncedSearchValue, pricingType],
    isError:
      pricingType === PricingType.CFA
        ? cfaListApiResponse.isError
        : customerListApiResponse.isError,

    paginationMeta: {
      startRecord:
        pricingType === PricingType.CFA
          ? cfaListApiResponse?.data?.startRecord || 0
          : customerListApiResponse?.data?.startRecord || 0,
      endRecord:
        pricingType === PricingType.CFA
          ? cfaListApiResponse?.data?.endRecord || 0
          : customerListApiResponse?.data?.endRecord || 0,
      totalCount:
        pricingType === PricingType.CFA
          ? cfaListApiResponse?.data?.totalCount || 0
          : customerListApiResponse?.data?.totalCount || 0,
    },
  });

  useEffect(() => {
    resetAll?.();
  }, [pathname]);

  const isLoading = useMemo(() => {
    return (
      pageNo === 1 &&
      (pricingType === PricingType.CFA
        ? cfaListApiResponse?.isUninitialized || cfaListApiResponse?.isLoading
        : customerListApiResponse?.isUninitialized ||
          customerListApiResponse?.isLoading)
    );
  }, [pageNo, pricingType, cfaListApiResponse, customerListApiResponse]);

  return {
    // Data
    customerOrCfaData,
    texts: PRICING_SCREEN_TEXTS[pricingType],

    // State
    showSearch,
    pageNo,
    isLoading,
    isPageLoading,
    pricingType,
    debouncedSearchValue,

    // Refs
    lastItemRef,

    // Handlers
    onScroll,
    setShowSearch,
    isEndOfList,
  };
};
